import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { credentialsAPI } from '../lib/api';
import LinkedInShareButtons from '../components/LinkedInShareButtons';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const READINESS_CONFIG = {
  fully_verified: { label: 'Fully Verified', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', icon: '\u2705' },
  partially_verified: { label: 'Partially Verified', color: '#0076A3', bg: '#FFE7D9', border: '#BBE3F5', icon: '\ud83d\udfe6' },
  initial: { label: 'Initial', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', icon: '\ud83d\udfe1' },
};

function ScoreRing({ score, max = 100, size = 80, color }) {
  const numericScore = (typeof score === 'number' && !Number.isNaN(score)) ? score : 0;
  const pct = max > 0 ? (numericScore / max) * 100 : 0;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F0EBE3" strokeWidth="6" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" fill={color}
        fontSize={size * 0.28} fontWeight="700" fontFamily="Cabinet Grotesk" style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
        {score != null ? score : '—'}
      </text>
    </svg>
  );
}

export default function CredentialPage() {
  const { tokenId } = useParams();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadToken = useCallback(async () => {
    try {
      const res = await credentialsAPI.get(tokenId);
      setToken(res.data);
    } catch (err) {
      toast.error('Credential not found');
    } finally { setLoading(false); }
  }, [tokenId]);

  useEffect(() => { loadToken(); }, [loadToken]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Verification link copied!');
  };

  if (loading) return <div style={{ minHeight: '100vh', background: '#F1EAE1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>Verifying credential...</div>;
  if (!token) return <div style={{ minHeight: '100vh', background: '#F1EAE1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>Credential not found</div>;

  const readiness = READINESS_CONFIG[token.readiness] || READINESS_CONFIG.initial;
  const creds = token.credentials || {};
  const bc = token.blockchain || {};

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0076A3 0%, #0076A3 40%, #F1EAE1 40%)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', paddingTop: '1rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden' }}>
              <img src="/chire-logo-full.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>
          <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '0.25rem' }}>Verified Credential Token</h1>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)' }}>Blockchain-verified candidate assessment record</p>
        </div>

        {/* NFT Card */}
        <Card style={{ background: '#fff', border: '2px solid ' + readiness.border, borderRadius: 20, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.1)' }}>
          {/* Top gradient bar */}
          <div style={{ height: 6, background: `linear-gradient(90deg, ${readiness.color}, #0076A3)` }} />

          <div style={{ padding: '2rem' }}>
            {/* Candidate Info + Readiness */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #0076A3, #00A0E0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>
                  {token.candidate_name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#0076A3' }}>{token.candidate_name}</h2>
                  <p style={{ fontSize: '0.8125rem', color: '#718096' }}>{token.candidate_email}</p>
                  {token.job_title && <p style={{ fontSize: '0.75rem', color: '#A0AEC0' }}>Evaluated for: {token.job_title}</p>}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{readiness.icon}</div>
                <Badge style={{ background: readiness.bg, color: readiness.color, border: `1px solid ${readiness.border}`, borderRadius: 999, fontSize: '0.6875rem', fontWeight: 600 }}>{readiness.label}</Badge>
                <div style={{ fontSize: '0.625rem', color: '#A0AEC0', marginTop: '0.25rem' }}>{token.stages_completed}/4 stages</div>
              </div>
            </div>

            {/* Score Rings */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
              {[
                { label: 'Screening', score: creds.screening?.score, color: '#0076A3' },
                { label: 'Assessment', score: creds.assessment?.score, color: '#0076A3' },
                { label: 'Interview', score: creds.interview?.overall_score, color: '#059669' },
                { label: 'Verification', score: creds.verification ? (creds.verification.clearance_status === 'cleared' ? 100 : 50) : null, color: '#D97706' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <ScoreRing score={s.score} color={s.color} size={72} />
                  <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#64748B', marginTop: '0.375rem' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Detailed Sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Screening */}
              {creds.screening && (
                <div style={{ padding: '1rem', borderRadius: 12, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Screening Results</h4>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Score: <strong>{creds.screening.score}/100</strong></span>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Fit: <strong>{creds.screening.fit}</strong></span>
                  </div>
                  {creds.screening.matched_skills?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {creds.screening.matched_skills.map(s => (
                        <span key={s} style={{ padding: '0.125rem 0.375rem', borderRadius: 999, background: 'rgba(16,185,129,0.1)', fontSize: '0.625rem', color: '#059669' }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Assessment */}
              {creds.assessment && (
                <div style={{ padding: '1rem', borderRadius: 12, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Assessment Results</h4>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Score: <strong>{creds.assessment.score}%</strong></span>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Points: <strong>{creds.assessment.total_score}/{creds.assessment.total_possible}</strong></span>
                  </div>
                  {creds.assessment.skill_breakdown && Object.keys(creds.assessment.skill_breakdown).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                      {Object.entries(creds.assessment.skill_breakdown).map(([skill, data]) => (
                        <div key={skill} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.6875rem', color: '#64748B', width: 80, flexShrink: 0 }}>{skill}</span>
                          <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#ECE7DE' }}>
                            <div style={{ height: '100%', borderRadius: 2, background: '#0076A3', width: `${((data.score || 0) / (data.max || 100)) * 100}%` }} />
                          </div>
                          <span style={{ fontSize: '0.625rem', color: '#0076A3', fontWeight: 600, width: 30, textAlign: 'right' }}>{data.score}/{data.max}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {creds.assessment.integrity?.risk_level && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.6875rem', color: creds.assessment.integrity.risk_level === 'low' ? '#059669' : '#D97706' }}>
                      Integrity: {creds.assessment.integrity.risk_level} risk
                    </div>
                  )}
                </div>
              )}

              {/* Interview */}
              {creds.interview && (
                <div style={{ padding: '1rem', borderRadius: 12, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Interview Results</h4>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Rating: <strong>{creds.interview.overall_rating}</strong></span>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Score: <strong>{creds.interview.overall_score}%</strong></span>
                    {creds.interview.communication_score && <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Communication: <strong>{creds.interview.communication_score}/10</strong></span>}
                  </div>
                  {creds.interview.recommendation && (
                    <div style={{ fontSize: '0.6875rem', color: '#0076A3', fontWeight: 600 }}>{creds.interview.recommendation}</div>
                  )}
                </div>
              )}

              {/* Verification */}
              {creds.verification && (
                <div style={{ padding: '1rem', borderRadius: 12, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Background Verification</h4>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Status: <strong style={{ color: creds.verification.clearance_status === 'cleared' ? '#059669' : '#D97706', textTransform: 'capitalize' }}>{creds.verification.clearance_status?.replace('_', ' ')}</strong></span>
                    <span style={{ fontSize: '0.75rem', color: '#4A5568' }}>Documents: <strong>{creds.verification.documents_submitted}</strong></span>
                  </div>
                </div>
              )}
            </div>

            {/* Blockchain Verification (minimal) */}
            {(() => {
              const cv = token.chain_verification || {};
              const minted = !bc.simulated && (bc.explorer_url || cv.on_chain);
              if (minted) {
                const statusLabel = cv.revoked ? 'Revoked' : (cv.hash_match ? 'Verified on-chain' : 'On-chain');
                const statusColor = cv.revoked ? '#FCA5A5' : '#6ee7b7';
                return (
                  <div data-testid="onchain-verification-panel" style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: 12, background: '#0076A3', color: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00A0E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                      <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.8125rem', fontWeight: 700 }}>On-Chain Verification</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.75rem' }}>
                      <div><span style={{ color: '#00A0E0' }}>Network:</span> <strong>{bc.network}</strong></div>
                      <div data-testid="onchain-status"><span style={{ color: '#00A0E0' }}>Status:</span> <span style={{ color: statusColor, fontWeight: 600 }}>{statusLabel}</span></div>
                    </div>
                    {bc.explorer_url && (
                      <a data-testid="basescan-link" href={bc.explorer_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.75rem', padding: '0.375rem 0.75rem', borderRadius: 8, background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.75rem', fontWeight: 600, textDecoration: 'none' }}>
                        View on BaseScan
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </a>
                    )}
                  </div>
                );
              }
              return (
                <div data-testid="record-only-panel" style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: 12, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
                    <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.8125rem', fontWeight: 700, color: '#0076A3' }}>Verified Record</span>
                  </div>
                  <p style={{ fontSize: '0.6875rem', color: '#718096' }}>On-chain anchoring pending{bc.network ? ` (${bc.network})` : ''}. This credential's cryptographic digest is recorded and ready to be minted on-chain.</p>
                </div>
              );
            })()}

            {/* Footer */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.6875rem', color: '#A0AEC0' }}>
                Issued: {new Date(token.issued_at).toLocaleDateString()} by {token.issued_by_name}
              </div>
              <button onClick={copyLink} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', borderRadius: 8, border: '1px solid #ECE7DE', background: '#FAF7F2', cursor: 'pointer', fontSize: '0.75rem', color: '#0076A3', fontWeight: 600 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                Share Link
              </button>
            </div>

            {/* Verified-candidate share / LinkedIn */}
            <div style={{ marginTop: '1.25rem', padding: '1rem', borderRadius: 12, background: '#F0F6FF', border: '1px solid #D6E4FF' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0A66C2', marginBottom: '0.25rem' }}>
                Showcase your verified credential
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: '0.75rem' }}>
                Add this CHIRE-verified credential to your LinkedIn profile or share it with your network.
              </div>
              <LinkedInShareButtons
                url={window.location.href}
                title={`CHIRE Verified Credential${token.job_title ? ' — ' + token.job_title : ''}`}
                certId={token.token_id || tokenId}
                issuedAt={token.issued_at}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
